import customLoadStoolItems, { ProductPriceParameter } from "@iewcasia/Store/Handlers/Custom/CustomLoadStoolItems";
import setSpoolModalOpen from "@iewcasia/Store/Handlers/Custom/CustomSetSpoolModal";
import { setGA4Event } from "@iewcasia/Store/Handlers/Utilities/CustomTracking";
import { CustomState } from "@iewcasia/Store/Reducers";
import { addToChainAfter, addToChainBefore, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    chain,
    ExecuteOnSuccessCallback,
    SendDataToApi,
} from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { CartLineCollectionModel, CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";

addToChainBefore(chain, SendDataToApi, async props => {
    const state = props.getState() as CustomState;
    // we filter out the items that have a productId that starts 00000000-...
    // since those are the items that are not on our db
    const productParameter = props.apiResult?.order.orderLines
        ?.map(line => ({
            productId: line.productId,
            qtyOrdered: line.qtyOrdered,
            unitOfMeasure: line.unitOfMeasure,
        }))
        .filter(line => !line.productId.startsWith("00000000-")) as ProductPriceParameter[];

    if (!productParameter || !productParameter.length) {
        return false;
    }

    // We make the call to load real time pricing to get the items
    // that have the additionalResults.spoolSelected set to "1" or not empty and we save them in a new array
    // we then update the cartLines to include the spools property for the items that
    // have the additionalResults.spoolSelected set to "1" or not empty

    const itemsWithStool = makeHandlerChainAwaitable<
        Parameters<typeof customLoadStoolItems>[0],
        ProductPriceParameter[]
    >(customLoadStoolItems);

    const itemsWithStools = await itemsWithStool({
        items: productParameter,
    })(props.dispatch, props.getState);

    const cartLines = getCurrentCartState(state).value?.cartLines;
    let orderLines = props.apiResult?.order.orderLines;
    let hasMatchingLine = false;
    
      // Verndale Custom Code - ticket: IEIXOP-4730
      if (cartLines?.length && orderLines?.length) {
        hasMatchingLine = orderLines?.some(orderLine =>
            cartLines.some(
                cl =>
                    cl.productId === orderLine.productId &&
                    cl.properties?.spoolLength !== undefined &&
                    cl.properties?.spoolSelected !== undefined
            )
        );
    
        if (hasMatchingLine) {
            orderLines = orderLines.filter(orderLine =>
                !cartLines.some(
                  cl =>
                    cl.productId === orderLine.productId &&
                    cl.properties?.spoolLength !== undefined &&
                    cl.properties?.spoolSelected !== undefined
                )
              );
        }
    }

    props.apiParameter = {
        cartLines: orderLines?.map(line => {
            const reorderItemHasStool = !!itemsWithStools?.find(item => item.productId === line.productId);
            if (reorderItemHasStool) {
                return {
                    productId: line.productId,
                    qtyOrdered: line.qtyOrdered,
                    unitOfMeasure: line.unitOfMeasure,
                    properties: {
                        spoolSelected: "1",
                        spoolLength:
                            typeof line.qtyOrdered === "number" && line.qtyOrdered > 0 ? `${line.qtyOrdered}` : "1",
                    },
                } as unknown as CartLineModel;
            }

            return {
                productId: line.productId,
                qtyOrdered: line.qtyOrdered,
                unitOfMeasure: line.unitOfMeasure,
            } as CartLineModel;
        }),
    } as CartLineCollectionModel;

    if (itemsWithStools && itemsWithStools.length > 0) {
        const settingsCollection = getSettingsCollection(props.getState());
        const showAddedToCartModal = (settingsCollection as any).customCartSettings?.showCustomAddToCartModal === true;
        // When the custom Added To Cart modal is enabled, show the spool notice there instead.
        if (showAddedToCartModal) {
            if (state.customSpoolModal.isOpen) {
                props.dispatch({
                    type: "Custom/SetSpoolModal/CompleteSetIsOpen",
                    isOpen: false,
                } as any);
            }
        } else if (!state.customSpoolModal.isOpen) {
            props.dispatch({
                type: "Custom/SetSpoolModal/CompleteSetIsOpen",
                isOpen: true,
                id: "order-history",
            } as any);
        } else {
            props.dispatch({
                type: "Custom/SetSpoolModal/CompleteSetIsOpen",
                isOpen: false,
            } as any);
        }
        (props as any).hasSpoolProducts = true;
        (props.apiParameter as any).hasSpoolProducts = true;
    }
});

addToChainAfter(chain, ExecuteOnSuccessCallback, props => {
    if (props.apiResult?.cartLines?.cartLines) {
        setGA4Event(props.apiResult?.cartLines?.cartLines, "add_to_cart");
    }

    const settingsCollection = getSettingsCollection(props.getState());
    const { showAddToCartConfirmationDialog } = settingsCollection.productSettings;
    const showAddedToCartModal = (settingsCollection as any).customCartSettings?.showCustomAddToCartModal === true;
    const addedCartLines = props.apiResult?.cartLines?.cartLines;
    if (showAddToCartConfirmationDialog && showAddedToCartModal && addedCartLines?.length) {
        const isQtyAdjusted = addedCartLines.some(cartLine => cartLine.isQtyAdjusted);
        const bulkProductCount = new Set(addedCartLines.map(cartLine => cartLine.productId)).size;
        const hasSpoolProducts =
            !!(props as any).hasSpoolProducts ||
            !!(props.apiParameter as any)?.hasSpoolProducts ||
            addedCartLines.some(
                cartLine =>
                    cartLine.properties?.spoolSelected !== undefined ||
                    cartLine.properties?.spoolLength !== undefined,
            );

        props.dispatch({
            type: "Custom/SetAddedToCartModal/Complete",
            isOpen: true,
            isBulkAdd: true,
            bulkProductCount,
            isQtyAdjusted,
            hasSpoolProducts,
        } as any);
    }
});
